// src/components/chat/styles/ChatStyles.ts
import styled from 'styled-components';

// Color palette
const colors = {
  primary: '#4A55A2',       // Deep blue
  secondary: '#7B66FF',     // Purple
  accent: '#5C469C',        // Dark purple
  lightPurple: '#D4B8FF',   // Light purple
  white: '#ffffff',
  lightGray: '#f8f9fa',
  gray: '#6c757d',
  error: '#dc3545',
  background: '#F5F5F5'
};

// Main container for the chat application
export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 600px;
  width: 100%;
  max-width: 450px;
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(74, 85, 162, 0.15);
  margin: 0 auto;
  
  @media (max-width: 768px) {
    max-height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
`;

// Header component styling
export const Header = styled.header`
  display: flex;
  align-items: center;
  padding: 1rem;
  background: ${colors.primary};
  color: ${colors.white};
  border-bottom: 2px solid ${colors.secondary};

  .avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: 0.75rem;
    background: ${colors.lightPurple};
  }

  .header-text {
    flex: 1;

    h1 {
      margin: 0;
      font-size: 1.2rem;
      font-weight: 600;
      color: ${colors.white};
    }

    p {
      margin: 0;
      font-size: 0.875rem;
      color: ${colors.lightPurple};
    }
  }
`;

// Message related components
interface MessageProps {
  isUser: boolean;
  isSystem?: boolean;
  status?: 'sending' | 'sent' | 'error';
}

export const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  overflow-y: auto;
  flex: 1;
  background: ${colors.background};
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${colors.lightGray};
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.accent};
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

export const Message = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isUser', 'isSystem', 'status'].includes(prop)
})<MessageProps>`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.isSystem ? 'center' : props.isUser ? 'flex-end' : 'flex-start'};
  max-width: ${props => props.isSystem ? '100%' : props.status === 'sent' ? '90%' : '80%'};
  align-self: ${props => props.isSystem ? 'center' : props.isUser ? 'flex-end' : 'flex-start'};

  .content {
    background: ${props => 
      props.isSystem ? colors.lightGray : 
      props.isUser ? colors.primary : colors.white
    };
    color: ${props => 
      props.isSystem ? colors.gray : 
      props.isUser ? colors.white : colors.primary
    };
    padding: ${props => props.isSystem ? '0.5rem 0.75rem' : '0.75rem 1rem'};
    border-radius: ${props => props.isSystem ? '0.5rem' : '1rem'};
    border-bottom-right-radius: ${props => props.isSystem ? '0.5rem' : props.isUser ? '0.25rem' : '1rem'};
    border-bottom-left-radius: ${props => props.isSystem ? '0.5rem' : !props.isUser ? '0.25rem' : '1rem'};
    margin-bottom: 0.25rem;
    opacity: ${props => props.status === 'sending' ? 0.7 : 1};
    word-wrap: break-word;
    max-width: 100%;
    box-shadow: ${props => props.isSystem ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.1)'};
    font-style: ${props => props.isSystem ? 'italic' : 'normal'};
    font-size: 0.875rem;
    text-align: ${props => props.isSystem ? 'center' : 'left'};
    
    /* Add left padding for AI responses */
    ${props => !props.isUser && !props.isSystem && `
      p, div {
        padding-left: 0.5rem;
      }
    `}
  }

  .timestamp {
    font-size: 0.75rem;
    color: ${colors.gray};
    margin: 0.25rem 0;
  }

  .retry-button {
    margin-top: 0.25rem;
    padding: 0.25rem 0.5rem;
    border: none;
    background: ${colors.error};
    color: ${colors.white};
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 0.75rem;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background: #c82333;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
  }
`;

// Input related components
export const InputContainer = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${colors.white};
  border-top: 2px solid ${colors.lightPurple};
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    gap: 0.375rem;
  }
`;

export const Input = styled.input`
  flex: 1;
  padding: 0.75rem;
  border: 2px solid ${colors.lightPurple};
  border-radius: 0.5rem;
  font-size: 1rem;
  outline: none;
  transition: all 0.15s ease-in-out;

  &:focus {
    border-color: ${colors.secondary};
    box-shadow: 0 0 0 0.2rem rgba(123, 102, 255, 0.25);
  }

  &:disabled {
    background: ${colors.lightGray};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${colors.gray};
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem;
    font-size: 16px; /* Prevents zoom on iOS */
  }
`;

export const SendButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: ${colors.secondary};
  color: ${colors.white};
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;

  &:hover:not(:disabled) {
    background: ${colors.accent};
    transform: translateY(-1px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(123, 102, 255, 0.25);
  }

  &:disabled {
    background: ${colors.gray};
    cursor: not-allowed;
    opacity: 0.65;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 1rem;
    min-width: 60px;
    font-size: 0.875rem;
  }
`;

// Typing indicator
export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  background: ${colors.white};
  border-radius: 1rem;
  width: fit-content;
  margin: 0.5rem 0;

  .dot {
    width: 0.5rem;
    height: 0.5rem;
    background: ${colors.secondary};
    border-radius: 50%;
    animation: bounce 1.4s infinite ease-in-out;

    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0); }
    40% { transform: scale(1); }
  }
`;

// Error message
export const ErrorMessage = styled.div`
  color: ${colors.error};
  padding: 0.5rem 1rem;
  margin: 0.5rem;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 0.25rem;
  text-align: center;
  font-size: 0.875rem;
`;

// Timestamp component
export const Timestamp = styled.span`
  font-size: 0.75rem;
  color: ${colors.gray};
  margin-top: 0.25rem;
`;

// Loading spinner
export const LoadingSpinner = styled.div`
  width: 1rem;
  height: 1rem;
  border: 2px solid ${colors.lightPurple};
  border-top: 2px solid ${colors.secondary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Powered by footer
export const PoweredBy = styled.div`
  padding: 2px 16px 4px;
  text-align: center;
  background: ${colors.white};
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  flex-shrink: 0;
  font-size: 11px;
  line-height: 1.2;
  
  a {
    font-size: 11px;
    color: ${colors.secondary};
    text-decoration: none;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 500;
    transition: color 0.2s ease;
    
    &:hover {
      color: ${colors.primary};
      text-decoration: underline;
    }
  }
`;
